import React, { useState, useEffect, useRef } from 'react';
import './PreGameReminder.scss';
import { getFormattedTime } from '../../epg/utils/shared/time';
import { getUpcomingEventsWithTeams } from '../../epg/utils/shared/events';
import { DAZN_LOGO_URL, getMatchupLabels } from '../../epg/utils/shared/matchup';
import FixedContentService from '../../services/FixedContentService';
import ScreenService from '../../services/ScreenService';
import StateService from '../../services/StateService';

const VISIBLE_MS = 8_000;
const EXIT_ANIMATION_MS = 400;
const HIDDEN_MS = 8 * 60_000;
const BLOCKING_STATES = new Set([
    'live_content',
    'scheduled_content',
    'socialzoom',
    'socialgrid3',
    'socialgrid3small'
]);

export default function PreGameReminder() {
    const [shownEvent, setShownEvent] = useState(null);
    const [exiting, setExiting] = useState(false);
    const pillState = useRef({ event: null, timer: null, exitTimer: null, visible: false, hiddenByBlocker: false, epgOpen: false });

    useEffect(() => {
        function isBlocked() {
            return FixedContentService.fixedContentOn
                || ScreenService.isVertical
                || pillState.current.epgOpen
                || BLOCKING_STATES.has(StateService.currentStateDef?.name)
                || BLOCKING_STATES.has(StateService.currentState?.name)
                || !!StateService.currentState?.state?.spotlight?.TrackingCode;
        }

        function clearTimer() {
            clearTimeout(pillState.current.timer);
            pillState.current.timer = null;
        }

        function clearExitTimer() {
            clearTimeout(pillState.current.exitTimer);
            pillState.current.exitTimer = null;
        }

        function scheduleShow(delay) {
            clearTimer();
            pillState.current.timer = setTimeout(showPill, delay);
        }

        function hidePill(immediate = false) {
            pillState.current.visible = false;
            clearExitTimer();
            if (immediate) {
                setExiting(false);
                setShownEvent(null);
                return;
            }
            setExiting(true);
            pillState.current.exitTimer = setTimeout(() => {
                setExiting(false);
                setShownEvent(null);
            }, EXIT_ANIMATION_MS);
        }

        function startVisiblePeriod(event) {
            clearTimer();
            pillState.current.hiddenByBlocker = false;
            pillState.current.visible = true;
            setShownEvent(event);
            setExiting(false);
            pillState.current.timer = setTimeout(() => {
                hidePill();
                scheduleShow(HIDDEN_MS);
            }, VISIBLE_MS);
        }

        function showPill() {
            if (!pillState.current.event) return;
            if (isBlocked()) {
                pillState.current.hiddenByBlocker = true;
                return;
            }
            startVisiblePeriod(pillState.current.event);
        }

        function clearCurrentEvent() {
            clearTimer();
            pillState.current.hiddenByBlocker = false;
            if (pillState.current.visible || pillState.current.exitTimer) hidePill(true);
        }

        function syncBlockedState() {
            if (isBlocked()) {
                if (!pillState.current.visible) return true;
                hidePill(true);
                pillState.current.hiddenByBlocker = true;
                scheduleShow(HIDDEN_MS);
                return true;
            }

            if (!pillState.current.hiddenByBlocker) return false;
            pillState.current.hiddenByBlocker = false;
            showPill();
            return false;
        }

        function onStateUpdate() {
            const nextEvent = getUpcomingEventsWithTeams()[0] ?? null;
            const { event: previousEvent, visible } = pillState.current;
            const eventChanged = previousEvent?.id !== nextEvent?.id;

            pillState.current.event = nextEvent;

            if (!nextEvent) {
                clearCurrentEvent();
                return;
            }
            if (!previousEvent) {
                showPill();
                return;
            }
            if (syncBlockedState()) return;
            if (eventChanged && visible) {
                startVisiblePeriod(nextEvent);
                return;
            }
            if (visible) setShownEvent(nextEvent);
        }

        function onBlockerUpdate() {
            syncBlockedState();
        }

        function onEPGStateUpdate(e) {
            pillState.current.epgOpen = !!e.detail?.isOpen;
            syncBlockedState();
        }

        function queryEPGOpen() {
            const detail = { isOpen: false };
            window.dispatchEvent(new CustomEvent('EPGStateQuery', { detail }));
            return detail.isOpen;
        }

        pillState.current.epgOpen = queryEPGOpen();

        const initial = getUpcomingEventsWithTeams();
        if (initial[0]) {
            pillState.current.event = initial[0];
            showPill();
        }

        window.addEventListener('UpshowStateUpdate', onStateUpdate);
        window.addEventListener('FixedContentUpdate', onBlockerUpdate);
        window.addEventListener('EPGStateUpdate', onEPGStateUpdate);
        return () => {
            clearTimer();
            clearExitTimer();
            window.removeEventListener('UpshowStateUpdate', onStateUpdate);
            window.removeEventListener('FixedContentUpdate', onBlockerUpdate);
            window.removeEventListener('EPGStateUpdate', onEPGStateUpdate);
        };
    }, []);

    if (!shownEvent) return null;

    const { firstTeam, secondTeam, schedule_start } = shownEvent;
    const timeLabel = getFormattedTime(schedule_start, 'h:mm A');
    const { firstLabel, secondLabel } = getMatchupLabels(firstTeam, secondTeam);

    return (
        <div className={`pregame-pill${exiting ? ' pregame-pill--exiting' : ''}`}>
            <img className="pregame-pill__logo" src={DAZN_LOGO_URL} alt="DAZN For Business" />
            <div className="pregame-pill__divider" aria-hidden="true" />
            <span className="pregame-pill__upcoming">UPCOMING</span>
            <div className="pregame-pill__divider" aria-hidden="true" />
            <img className="pregame-pill__team-logo" src={firstTeam.logo} alt={firstTeam.display_name} />
            <span className="pregame-pill__team-name">{firstLabel}</span>
            <span className="pregame-pill__vs">vs</span>
            <span className="pregame-pill__team-name">{secondLabel}</span>
            <img className="pregame-pill__team-logo" src={secondTeam.logo} alt={secondTeam.display_name} />
            <div className="pregame-pill__divider" aria-hidden="true" />
            <span className="pregame-pill__time">{timeLabel}</span>
        </div>
    );
}
